<template>
  <div class="columns is-multiline">
    <div class="column is-12">
      <div class="columns is-multiline">
        <div class="column" v-if="!isOutdated">
          <SimilarListingsControls />
        </div>
        <div class="column is-12-mobile is-narrow-tablet" v-if="!isOutdated">
          <SimilarListingsSort :location="activeFilters.includes('distance')" />
        </div>
        <div
          class="column is-12 has-background-white is-sticky"
          v-if="!isOutdated"
        >
          <div v-if="listing && !listing.sold && listing.listingPrice">
            <p class="title is-6 has-text-dark">
              <span>{{ vrm }} is currently for sale</span>
            </p>
            <VehicleListing
              class="mb-5"
              v-bind="{ listing, isListing: true }"
            />
          </div>
          <div v-if="total" class="market-overview-summary my-4">
            <p class="title is-6 has-text-dark mb-0 market-overview-summary__total">
              {{ total }} similar vehicles found ({{ onSale }} currently for
              sale)
            </p>
            <SimilarListingsAveragePrice
              class="market-overview-summary__average"
            />
          </div>
          <div class="is-hidden-touch">
            <SimilarListingsHeaders class="px-4" />
          </div>
        </div>
        <div v-if="loading" class="column is-12">
          <div v-for="i in 10" :key="i" class="mb-4">
            <div class="box">
              <div class="columns is-multiline">
                <div class="column is-12">
                  <ContentPlaceholder />
                </div>
                <div class="column is-4">
                  <ContentPlaceholder />
                </div>
                <div class="column is-4">
                  <ContentPlaceholder />
                </div>
                <div class="column is-4">
                  <ContentPlaceholder />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="column is-12"
          v-else-if="listings && listings.length"
          v-for="(listing, index) in listings"
          :key="index"
        >
          <VehicleListing v-bind="{ listing }" />
        </div>
        <div
          class="column is-12"
          v-if="!isEmpty && listings && listings.length"
        >
          <Pagination
            v-bind="{
              page: $store.getters['companion/similarListings/page'],
              pages
            }"
            :fn="'fetch'"
            @change="setPage"
            :disabled="loading"
          />
        </div>
      </div>
      <div
        class="columns is-vcentered has-text-centered"
        v-if="isOutdated && !loading"
      >
        <div class="column is-12">
          <TableEmpty
            text="VRM no longer registered to vehicle"
            subtext="Please search for the new VRM to find similar listings"
          />
        </div>
      </div>
      <div
        class="columns is-vcentered has-text-centered"
        v-else-if="isEmpty && !loading"
      >
        <div class="column is-12">
          <TableEmpty
            text="No listings available"
            :subtext="`We couldn't find a similar ${make} with this criteria`"
          />
        </div>
      </div>
    </div>
    <transition enter-active-class="animated rubberBand">
      <div class="back-to-top" v-if="listings">
        <button
          @click="scrollToTop"
          class="button is-medium is-info"
          title="Back to top"
          v-tippy="{ placement: 'left' }"
        >
          <span class="icon">
            <i class="fal fa-arrow-circle-up" />
          </span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'CompanionMarketOverview',
  data: () => ({
    timeout: 0
  }),
  components: {
    TableEmpty: () => import('core/components/TableEmpty'),
    SimilarListingsHeaders: () =>
      import(
        'modules/companion/components/similar-listings/SimilarListingsHeaders'
      ),
    SimilarListingsControls: () =>
      import(
        'modules/companion/components/similar-listings/SimilarListingsControls'
      ),
    SimilarListingsSort: () =>
      import(
        'modules/companion/components/similar-listings/SimilarListingsSort'
      ),
    SimilarListingsAveragePrice: () =>
      import(
        'modules/companion/components/similar-listings/SimilarListingsAveragePrice'
      ),
    VehicleListing: () =>
      import('modules/companion/components/section/VehicleListing'),
    ContentPlaceholder: () => import('core/components/ContentPlaceholder'),
    Pagination: () => import('core/modules/pagination/components/Pagination')
  },
  methods: {
    ...mapActions({
      fetch: 'companion/similarListings/fetch',
      clearAndFetch: 'companion/similarListings/clearAndFetch'
    }),
    async setPage(page) {
      try {
        await this.fetch({ page })
      } catch (err) {
        this.$notify('Listings unavailable')
      }
    },
    scrollToTop() {
      return window.scrollTo(0, 0)
    }
  },
  computed: {
    isEmpty() {
      return Array.isArray(this.listings) && this.listings.length === 0
    },
    pages() {
      return Math.ceil(this.total / 10) // Using 10 as limit
    },
    ...mapGetters('companion', [
      'make',
      'listing',
      'vrm',
      'isOutdated',
      'specs'
    ]),
    ...mapGetters('companion/similarListings', [
      'listings',
      'total',
      'page',
      'sortBy',
      'loading',
      'onSale'
    ]),
    ...mapGetters({
      activeFilters: 'companion/similarListings/filterBy/active'
    })
  }
}
</script>

<style lang="sass" scoped>
.back-to-top
  position: fixed
  right: 1rem
  bottom: 1rem
  z-index: 1
.is-sticky
  box-shadow: 0 1rem 1rem -1rem $grey-lightest
  z-index: 1
  position: sticky
  top: 0
.market-overview-summary
  display: flex
  justify-content: space-between
  align-items: center
  @media (max-width: 1023px)
    flex-direction: column
    align-items: flex-start
    gap: 0.5rem
  @media (min-width: 1024px)
    &__total
      margin-left: 1rem
    &__average
      margin-right: 1rem
</style>

<style lang="sass">
.vrm-column
  max-width: 8.5rem
.year-column
  max-width: 5rem
.colour-column
  max-width: 7rem
.category-column
  max-width: 7rem
.dealer-column
  max-width: 11rem

.pagination
  .pagination-link, .pagination-previous, .pagination-next
    &[disabled]
      opacity: 0.5
      cursor: not-allowed
</style>